import { ImportanceBadge } from '@/components/ui/importance-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { Sparkles, MessageSquare } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { DiscoveryCard } from './discovery-card';
import { cn } from '@/lib/utils';

interface ScholarlyDiscussionCardProps {
    discussion: {
        title: string;
        summary: string;
        relevance: string;
        viewpoints: Array<{
            position: string;
            description: string;
            proponents?: string[];
            confidence?: 'majority' | 'minority' | 'debated';
        }>;
        importance?: Importance;
        verseRef?: string;
        sources?: Source[];
    };
    bookName?: string;
    isExpanded: boolean;
    onToggleExpansion: () => void;
    thumbnailUrl?: string;
}

export function ScholarlyDiscussionCard({
    discussion,
    bookName,
    isExpanded,
    onToggleExpansion,
    thumbnailUrl,
}: ScholarlyDiscussionCardProps) {
    const isDarkMode = useDarkMode();

    const getConfidenceBadgeColor = (confidence?: 'majority' | 'minority' | 'debated') => {
        switch (confidence) {
            case 'majority':
                return isDarkMode
                    ? 'bg-green-500/10 text-green-400 border-green-500/20'
                    : 'bg-green-500/10 text-green-700 border-green-500/20';
            case 'minority':
                return isDarkMode
                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    : 'bg-amber-500/10 text-amber-700 border-amber-500/20';
            case 'debated':
                return isDarkMode
                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                    : 'bg-blue-500/10 text-blue-700 border-blue-500/20';
            default:
                return 'bg-muted/20 text-muted-foreground border-muted/40';
        }
    };

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            icon={MessageSquare}
            header={
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 relative z-10">
                    <div className="flex flex-col gap-3 flex-1 min-w-0">
                        {discussion.verseRef && bookName && (
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/40">
                                {bookName} {discussion.verseRef}
                            </div>
                        )}

                        <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight">
                            {discussion.title}
                        </h4>

                        {discussion.summary && (
                            <div className="space-y-3">
                                <p className={`text-sm text-muted-foreground/80 leading-relaxed italic font-light ${!isExpanded ? 'line-clamp-4' : ''}`}>
                                    {discussion.summary}
                                </p>
                                {!isExpanded && (
                                    <>
                                        {discussion.viewpoints?.length > 0 && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] uppercase tracking-widest bg-muted/20 px-2 py-1 rounded text-muted-foreground">
                                                    {discussion.viewpoints.length} {discussion.viewpoints.length === 1 ? 'Viewpoint' : 'Viewpoints'}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-primary/40 group-hover/card:text-primary/70 transition-colors">
                                            <Sparkles className="h-2.5 w-2.5" />
                                            <span>Explore Discussion</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-6 mt-6 relative z-10">
                {/* Viewpoints */}
                {discussion.viewpoints?.length > 0 && (
                    <div className="space-y-4">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Scholarly Viewpoints</div>
                        {discussion.viewpoints.map((viewpoint, index) => (
                            <div key={index} className="space-y-2 pl-4 border-l border-primary/10">
                                <div className="flex items-start justify-between gap-3">
                                    <h5 className="text-sm font-semibold text-foreground/80">{viewpoint.position}</h5>
                                    {viewpoint.confidence && (
                                        <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${getConfidenceBadgeColor(viewpoint.confidence)}`}>
                                            {viewpoint.confidence}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-foreground/70 leading-relaxed font-light">{viewpoint.description}</p>
                                {viewpoint.proponents && viewpoint.proponents.length > 0 && (
                                    <div className="text-[11px] text-muted-foreground italic pt-0.5">
                                        Proponents: {viewpoint.proponents.join(', ')}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Relevance */}
                {discussion.relevance && (
                    <div className="space-y-2 px-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 flex items-center gap-2">
                            <Sparkles className="h-3 w-3" />
                            Why It Matters
                        </div>
                        <p className="text-sm text-foreground/70 font-light leading-relaxed italic border-l-2 border-primary/20 pl-4 py-0.5">{discussion.relevance}</p>
                    </div>
                )}

                {/* Footer Metadata */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-border/10">
                    <div className="flex items-center gap-3 flex-wrap">
                        {discussion.importance && (
                            <ImportanceBadge importance={discussion.importance} context="cultural" />
                        )}
                    </div>

                    {discussion.sources?.length > 0 && (
                        <SourceLinks sources={discussion.sources} />
                    )}
                </div>
            </div>
        </DiscoveryCard>
    );
}
